import React, { useEffect, useMemo, useRef } from "react";
import { Box } from "@material-ui/core";
import { useDeviceTypes, useTrackingEventControl } from "halifax";
import { Route } from "react-router-dom";
import { RouteComponentProps } from "react-router";
import clsx from "clsx";

import {
  HotelBookType,
  REVIEW_DETAILS_HOTEL_CHECKOUT,
  getReviewDetailsHotelCheckoutHotelCfarOfferFacts,
  getPromotionTrackingProperties,
  CoreStaysCheckout,
  GetCfarQuotesResponseEnum,
  RoomProduct,
} from "redmond";
import dayjs from "dayjs";
import { trackEngagementEvent, useIsTravelApp } from "@capone/common";
import { useDispatch } from "react-redux";
import { HotelBookConnectorProps } from "./container";
import {
  BookingErrorModal,
  BookingInProgressModal,
  BookingSuccessModal,
} from "./components";
import { PATH_BOOK, PATH_BOOK_CONFIRMATION } from "../../utils/paths";
import "./styles.scss";
import { trackEvent } from "../../api/v0/analytics/trackEvent";
import {
  AVAILABLE,
  getExperimentVariant,
  TRAVEL_CREDIT_HISTORY_EXPERIMENT,
  TRAVEL_WALLET_OFFER_EXPERIMENT,
  useExperiments,
} from "../../context/experiments";
import { HOTEL_CHECKOUT_TITLE, PORTAL_TITLE } from "../../lang/textConstants";
import { goToShop } from "../shop/utils/queryStringHelpers";
import { config } from "../../api/config";
import {
  setHotelShop,
  setCrossSellTrackingProperties,
} from "../shop/actions/actions";
import { setSearch } from "../search/actions/actions";
import {
  setCfarQuotes,
  setHasSelectedRefundableRoom,
  setSelectedCfarId,
} from "../ancillary/actions/actions";
import { setHotelAvailability } from "../availability/actions/actions";
import { CallState } from "../travel-wallet/reducer";

const {
  DesktopHotelBookWorkflow,
} = require(`./components/${config.TENANT}/DesktopHotelBookWorkflow`);
const {
  MobileHotelBookWorkflow,
} = require(`./components/${config.TENANT}/MobileHotelBookWorkflow`);

export interface IHotelBookProps
  extends RouteComponentProps,
    HotelBookConnectorProps {
  core?: CoreStaysCheckout;
}

export const HotelBook = (props: IHotelBookProps) => {
  const {
    history,
    location,
    chosenProduct,
    selectedLodging,
    reviewHotelDetailsProperties,
    hotelCfarQuote,
    additionalInfo,
    closeSession,
    resetBook,
    setHotelBookType,
    getHotelCfarOfferChoiceProperties,
    fetchApplicableTravelWalletItems,
    fetchTravelWalletCreditHistory,
    fetchTravelWalletDetails,
    walletDetailsCallState,
    priceQuote,
    checkInDate,
    checkOutDate,
    tripTotal,
    accountReferenceIds,
    searchLocation,
    core,
    setIsTravelApp,
  } = props;

  const [EDSEventSent, setEDSEventSent] = React.useState<boolean>(false);

  const { matchesMobile, matchesDesktop } = useDeviceTypes();
  const {
    trackingEventControl,
    updateTrackingEventControl,
    removeEventFromTrackingEventControl,
  } = useTrackingEventControl();
  const expState = useExperiments();

  const travelWalletOffer = getExperimentVariant(
    expState.experiments,
    TRAVEL_WALLET_OFFER_EXPERIMENT
  );
  const isTravelWalletOfferExperiment = useMemo(
    () => travelWalletOffer === AVAILABLE,
    [travelWalletOffer]
  );

  const travelCreditHistoryExperiment = getExperimentVariant(
    expState.experiments,
    TRAVEL_CREDIT_HISTORY_EXPERIMENT
  );
  const isTravelCreditHistoryExperiment = useMemo(
    () => travelCreditHistoryExperiment === AVAILABLE,
    [travelCreditHistoryExperiment]
  );

  const dispatch = useDispatch();
  useEffect(() => {
    if (core != null) {
      dispatch(
        setSearch({
          fromDate: dayjs(core.search.dateRange.from).toDate(),
          untilDate: dayjs(core.search.dateRange.until).toDate(),
          roomsCount: core.search.rooms,
        })
      );

      dispatch(
        setHotelAvailability({
          searchFromDate: dayjs(core.search.dateRange.from).toDate(),
          searchUntilDate: dayjs(core.search.dateRange.until).toDate(),
          searchAdultsCount: core.search.guests.adults,
          searchChildren: core.search.guests.children,
          searchPetsCount: core.search.guests.pets ? 1 : 0,
          searchRoomsCount: core.search.rooms,
        })
      );

      dispatch(
        setHotelShop({
          selectedLodging: core.lodging,
          roomInfoProducts: [core.roomInfo],
          selectedRoomId: core.roomInfo.roomInfo.roomId,
          selectedRateId: core.roomProduct.rateId?.value,
          chosenRoomInfoIndex: 0,
          chosenProductIndex: core.roomInfo.products.findIndex(
            (p: RoomProduct) =>
              p.rateId?.value === core.roomProduct.rateId?.value
          ),
          hotelShopLodgingSelection: core.lodgingSelection,
          cancellationSummary: core.cancellationSummary,
        })
      );

      if (core.crossSellProperties != null) {
        dispatch(
          setCrossSellTrackingProperties(core.crossSellProperties)
        );
      }

      dispatch(
        setCfarQuotes({
          response: {
            GetCfarQuotesResponse: GetCfarQuotesResponseEnum.Success,
            lodgingId: core.lodging.lodging.id,
            roomInfoCfarQuotes:
              core.cfarOffer != null || core.HTSCfarOffer != null
                ? [
                    {
                      roomInfo: core.roomInfo.roomInfo,
                      cfarQuotes: [core.cfarOffer ?? core.HTSCfarOffer],
                    },
                  ]
                : [],
            shouldDisableRefundableRooms: false,
          },
        })
      );
      const selectedOffer = core.cfarOffer ?? core.HTSCfarOffer;

      dispatch(
        setSelectedCfarId(
          selectedOffer != null
            ? {
                value: selectedOffer.id,
                offerType: selectedOffer.offerType.CfarOfferType,
              }
            : null
        )
      );
      dispatch(
        setHasSelectedRefundableRoom(
          selectedOffer?.merchandisingPlacement?.MerchandisingPlacement ===
            "RoomsList"
        )
      );
    }
  }, []);

  useEffect(() => {
    if ((!!chosenProduct && !!selectedLodging) || core != null) {
      setHotelBookType(HotelBookType.DEFAULT);
    } else {
      goToShop({ history });
    }
  }, []);

  // note: experiments will always return `control` until `/api/v0/experiments/active` has returned
  useEffect(() => {
    if (!!chosenProduct && !!selectedLodging) {
      isTravelWalletOfferExperiment && fetchApplicableTravelWalletItems();
      isTravelCreditHistoryExperiment && fetchTravelWalletCreditHistory();
      if (walletDetailsCallState === CallState.NotCalled) {
        fetchTravelWalletDetails();
      }
    }
  }, [
    chosenProduct,
    selectedLodging,
    isTravelWalletOfferExperiment,
    isTravelCreditHistoryExperiment,
  ]);

  useEffect(() => {
    if (!!chosenProduct && !!priceQuote) {
      if (
        chosenProduct.tripTotal.fiat.value !==
        priceQuote.pricing.tripTotal.fiat.value
      ) {
        isTravelWalletOfferExperiment && fetchApplicableTravelWalletItems();
      }
    }
  }, [chosenProduct, priceQuote]);

  useEffect(() => {
    setTimeout(() => window.scrollTo(0, 0), 0);
  }, [location.pathname]);

  // Wait for the chosen product: when coming from core, it is dispatched in an effect after the first render.
  const hasQueuedReviewDetailsEvent = useRef(false);

  useEffect(() => {
    if (hasQueuedReviewDetailsEvent.current || chosenProduct == null) {
      return;
    }
    hasQueuedReviewDetailsEvent.current = true;

    const offerProperties = {
      viewed_travel_offer: !!selectedLodging?.bestOfferThisLodging,
    };
    updateTrackingEventControl({
      [REVIEW_DETAILS_HOTEL_CHECKOUT]: {
        properties: {
          ...reviewHotelDetailsProperties.properties,
          ...getReviewDetailsHotelCheckoutHotelCfarOfferFacts({
            roomProduct: chosenProduct,
            hotelCfarQuote,
            additionalInfo,
            ...getHotelCfarOfferChoiceProperties("book"),
          }),
          ...offerProperties,
        },
        encryptedProperties: [
          ...reviewHotelDetailsProperties.encryptedProperties,
        ],
      },
    });
  }, [
    chosenProduct,
    selectedLodging,
    reviewHotelDetailsProperties,
    hotelCfarQuote,
    additionalInfo,
    getHotelCfarOfferChoiceProperties,
    updateTrackingEventControl,
  ]);

  useEffect(() => {
    document.title = HOTEL_CHECKOUT_TITLE;
    return () => {
      document.title = PORTAL_TITLE;
    };
  }, []);

  useEffect(() => {
    return () => {
      const resetCheckoutStates = () => {
        // note: close off any opening session upon leaving the book page
        closeSession();
        // note: calling resetBook (on dismount) erases any checkout states upon leaving the book page
        resetBook();
      };

      resetCheckoutStates();
    };
  }, []);

  useEffect(() => {
    if (trackingEventControl[REVIEW_DETAILS_HOTEL_CHECKOUT]) {
      const offerProperties = {
        viewed_travel_offer: !!selectedLodging?.bestOfferThisLodging,
      };
      trackEvent({
        eventName: REVIEW_DETAILS_HOTEL_CHECKOUT,
        properties: {
          ...trackingEventControl[REVIEW_DETAILS_HOTEL_CHECKOUT].properties,
          ...offerProperties,
        },
        encryptedProperties:
          trackingEventControl[REVIEW_DETAILS_HOTEL_CHECKOUT]
            .encryptedProperties,
      });
      removeEventFromTrackingEventControl(REVIEW_DETAILS_HOTEL_CHECKOUT);
    }
  }, [trackingEventControl]);

  useEffect(() => {
    if (
      !EDSEventSent &&
      selectedLodging &&
      location.pathname.startsWith(PATH_BOOK) &&
      !!tripTotal
    ) {
      trackEngagementEvent({
        event: {
          event_type: "hotel_checkout",
          ...getPromotionTrackingProperties(selectedLodging),
          lodging_id: selectedLodging?.lodging.id,
          lodging_name: selectedLodging?.lodging.name,
          lodging_type: selectedLodging?.lodgingCollection,
          advance: dayjs(checkInDate).diff(dayjs(), "day"),
          lodging_address:
            selectedLodging?.lodging?.address &&
            "line1" in selectedLodging?.lodging?.address
              ? selectedLodging?.lodging.address.line1
              : undefined,
          lodging_city: selectedLodging?.lodging.city,
          lodging_country: selectedLodging?.lodging.country,
          coordinates: selectedLodging?.lodging.location.coordinates,
          length_of_stay: dayjs(checkOutDate).diff(checkInDate, "day"),
          market_name_full: searchLocation?.label || "", // TODO: Use real market name in place of search term when available: https://hopper-jira.atlassian.net/browse/CMKT-3678
          owed_overall_including_taxes_fees: tripTotal?.fiat,
          check_in_date_timestamp: checkInDate?.getTime(),
          check_out_date_timestamp: checkOutDate?.getTime(),
          account_reference_ids: accountReferenceIds,
        },
      });

      setEDSEventSent(true);
    }
  }, [selectedLodging, tripTotal]);

  const { isTravelApp } = useIsTravelApp();

  useEffect(() => {
    setIsTravelApp(!!isTravelApp && matchesMobile);
  }, [isTravelApp]);

  return (
    <Box
      className={clsx(
        "hotel-book-root",
        { confirm: location.pathname === PATH_BOOK_CONFIRMATION },
        { mobile: matchesMobile }
      )}
    >
      <Route path={PATH_BOOK} exact>
        <>
          <Box className="hotel-book-container">
            {matchesDesktop && <DesktopHotelBookWorkflow core={core} />}
            {matchesMobile && <MobileHotelBookWorkflow />}
          </Box>
          <BookingErrorModal />
          <BookingInProgressModal />
        </>
      </Route>
      <Route path={PATH_BOOK_CONFIRMATION}>
        <BookingSuccessModal />
      </Route>
    </Box>
  );
};
